"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Maximize2, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
const galleryItems = [{
  id: "photo-1",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=hhw988444dm",
  title: "Mobile Cold Storage at an Event Venue",
  caption: "Refrigerated and freezer trailer positioned for event food and beverage storage.",
  details: "Mobile refrigeration trailer set up at an outdoor event.",
  alt: "Fridge Fleet mobile refrigeration trailer parked at an outdoor event venue.",
  tag: "Photo"
}, {
  id: "photo-2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_20-PM-1.png",
  title: "Trailer at a Commercial Loading Area",
  caption: "Mobile refrigeration trailer positioned for temporary commercial cold-storage needs",
  details: "Mobile refrigeration trailer parked at a commercial loading dock.",
  alt: "Fridge Fleet mobile refrigeration trailer parked at a warehouse loading dock.",
  tag: "Photo"
}, {
  id: "photo-3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_08-PM-1.png",
  title: "Mobile Refrigeration at a Bakery",
  caption: "Refrigerated trailer positioned outside a bakery for additional food-service cold storage.",
  details: "Mobile refrigeration trailer parked outside a retail bakery.",
  alt: "Fridge Fleet mobile refrigeration trailer parked outside a bakery storefront.",
  tag: "Photo"
}, {
  id: "photo-4",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_54-PM-1.png",
  title: "Mobile Cold Storage for Outdoor Events",
  caption: "Refrigerated and freezer trailer positioned for food and beverage storage at an outdoor festival or special event",
  details: "Mobile refrigeration trailer deployed at a festival/production site.",
  alt: "Fridge Fleet mobile refrigeration trailer parked backstage at an outdoor festival.",
  tag: "Photo"
}, {
  id: "photo-5",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=r8nu43mm3e",
  title: "Refrigeration Support for Food Service",
  caption: "Mobile cold storage trailer positioned near a restaurant for additional food and beverage storage",
  details: "Mobile refrigeration trailer parked at a restaurant's receiving dock.",
  alt: "Fridge Fleet mobile refrigeration trailer parked at a restaurant receiving dock.",
  tag: "Photo"
}, {
  id: "photo-6",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png",
  tag: "53-Foot Reefer",
  title: "53-Foot Reefer Trailer",
  caption: "Large-capacity temporary refrigerated storage option. Availability, specifications, delivery requirements, intended use, and temperature requirements are confirmed individually.",
  details: "Fridge Fleet 53-foot reefer trailer shown in a potential cold-storage application.",
  alt: "Fridge Fleet 53-foot reefer trailer for temporary refrigerated storage"
}];
export default function Gallery() {
  const [activeItem, setActiveItem] = useState<(typeof galleryItems)[0] | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="photo-gallery" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/40 text-accent-foreground text-xs font-bold tracking-widest uppercase mb-4"><span className="size-2 rounded-full bg-primary"></span>Fleet Gallery</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">Refrigerated, Freezer &amp; Cold Storage Trailer Gallery</h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">View Fridge Fleet's 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus the 53-foot reefer trailer, shown in potential temporary cold-storage applications. Trailer availability, specifications, and suitability are confirmed individually.</p>
        </div>

        {/* 3x2 Photo Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {galleryItems.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="group flex flex-col bg-card border border-border rounded-lg overflow-hidden shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
              {/* Image Container with Zoom Trigger */}
              <Button type="button" variant="ghost" onClick={() => setActiveItem(item)} aria-label={`Enlarge photo: ${item.title}`} className="relative w-full h-auto p-0 aspect-4/3 overflow-hidden bg-muted cursor-pointer text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-none hover:bg-transparent">
                <img className="w-full h-full object-cover" src={item.image} alt={item.alt} />

                {/* Tag Overlay */}
                <div className="absolute top-3 left-3 bg-secondary/85 backdrop-blur-xs text-secondary-foreground text-xs font-medium px-2.5 py-1 rounded-sm">
                  {item.tag}
                </div>

                {/* Zoom Icon Button Overlay */}
                <div className="absolute top-3 right-3 size-8 rounded-full bg-background/90 text-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-xs">
                  <Maximize2 className="size-4" />
                </div>
              </Button>

              {/* Caption and Meta */}
              <div className="p-4 sm:p-5 flex flex-col justify-between flex-1">
                <div>
                  <h2 className="text-base font-semibold text-card-foreground font-default">
                    {item.title}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {item.caption}
                  </p>
                  {item.id === "photo-6" && <div className="mt-2.5">
                      <Link to="/FootRefrigeratedTrailerRentalsPage" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                        <span>View the 53-foot reefer trailer</span>
                        <ArrowRight className="size-3" />
                      </Link>
                    </div>}
                </div>

                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-end text-xs text-muted-foreground">
                  <Button type="button" variant="link" onClick={() => setActiveItem(item)} className="h-auto p-0 font-medium text-primary hover:underline inline-flex items-center gap-1 cursor-pointer text-xs">
                    Expand view
                  </Button>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Clarification Note */}
        <p className="mt-6 md:mt-8 text-center text-sm text-muted-foreground">
          Application settings shown are illustrative. Trailer size, availability, specifications, and suitability are confirmed individually.
        </p>

        {/* Bottom Dispatch Action Strip */}
        <div className="mt-12 md:mt-16 bg-card border border-border rounded-lg p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1.5">
            <h2 className="text-lg sm:text-xl font-bold text-card-foreground font-default max-w-xl">Need mobile cold storage? Request refrigerated or freezer trailer availability in our listed Southern California and Las Vegas-area communities.</h2>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild className="w-full sm:w-auto">
              <Link to="/ContactRequestAQuotePage">
                <span>Request a Quote</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="secondary" className="w-full sm:w-auto">
              <a href="tel:2135218821" className="inline-flex items-center justify-center gap-2">
                <Phone className="size-4" />
                <span>213-521-8821</span>
              </a>
            </Button>
          </div>
        </div>

        {/* Accessible Modal Zoom Preview */}
        <Dialog open={!!activeItem} onOpenChange={open => !open && setActiveItem(null)}>
          {activeItem && <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-3xl p-0 overflow-hidden bg-card border-border">
              <div className="relative w-full aspect-16/10 bg-muted overflow-hidden border-b border-border">
                <img className="w-full h-full object-cover" src={activeItem.image} alt={activeItem.alt} />
                <span className="absolute bottom-3 left-3 bg-secondary/90 backdrop-blur-xs text-secondary-foreground text-xs font-semibold px-2.5 py-1 rounded-sm">
                  {activeItem.tag}
                </span>
              </div>

              <div className="p-6">
                <DialogHeader className="text-left">
                  <DialogTitle className="text-xl font-bold text-card-foreground font-default">
                    {activeItem.title}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-muted-foreground mt-1">
                    {activeItem.details}
                  </DialogDescription>
                </DialogHeader>

                {activeItem.id === "photo-6" && <div className="mt-3">
                    <Link to="/FootRefrigeratedTrailerRentalsPage" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                      <span>View the 53-foot reefer trailer</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>}

                <div className="mt-6 pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-muted-foreground">
                    Southern California &amp; Las Vegas Area coverage
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button asChild size="sm" className="w-full sm:w-auto">
                      <Link to="/ContactRequestAQuotePage">
                        Request a Quote
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>}
        </Dialog>
      </div>
    </section>;
}